import { useCallback, useMemo } from 'react';

import { useTracking } from '@frontend/tracking';

type ClickSource = 'tray' | 'inbox' | 'toast';
type DismissSource = 'tray' | 'overflow_menu';
type MarkReadSource = 'tray' | 'inbox' | 'overflow_menu';
type BulkAction = 'mark_as_read' | 'mark_as_unread' | 'dismiss';
type PreferenceChannel = 'in_app' | 'email';
type FilterType = 'agent' | 'category' | 'priority' | 'time' | 'status';

export function useNotificationTracking() {
  const { track } = useTracking();

  const trayViewed = useCallback(
    (badgeCount: number) => track('Notification Tray Viewed', { badge_count: badgeCount }),
    [track]
  );

  const inboxViewed = useCallback(
    (badgeCount: number) => track('Notification Inbox Viewed', { badge_count: badgeCount }),
    [track]
  );

  const clicked = useCallback(
    (type: string, source: ClickSource) => track('Notification Clicked', { type, source }),
    [track]
  );

  const bulkAction = useCallback(
    (count: number, action: BulkAction) => track('Notification Bulk Action', { count, action }),
    [track]
  );

  const markedAsRead = useCallback(
    (count: number, source: MarkReadSource) =>
      track('Notification Marked As Read Button', { count, source }),
    [track]
  );

  const dismissed = useCallback(
    (type: string, source: DismissSource) => track('Notification Dismissed', { type, source }),
    [track]
  );

  const preferenceModified = useCallback(
    (channel: PreferenceChannel, enabled: boolean, type: string) =>
      track('Notification Preference Modified', { type, channel, enabled }),
    [track]
  );

  const filterApplied = useCallback(
    (filterType: FilterType) => track('Notification Filter Applied', { filter_type: filterType }),
    [track]
  );

  return useMemo(
    () => ({
      trayViewed,
      inboxViewed,
      clicked,
      bulkAction,
      markedAsRead,
      dismissed,
      preferenceModified,
      filterApplied,
    }),
    [
      trayViewed,
      inboxViewed,
      clicked,
      bulkAction,
      markedAsRead,
      dismissed,
      preferenceModified,
      filterApplied,
    ]
  );
}
